/**
 * Helpers for the self-serve custom-domain flow.
 *
 * The live `custom_domain` is platform-controlled; org admins REQUEST a domain
 * and follow DNS steps. These helpers normalise input, validate it, build the
 * tenant's public URLs, and describe the DNS records to add at their registrar.
 */

/** Default DNS targets for the platform host (Vercel). Override per-platform. */
export const DEFAULT_DOMAIN_DNS = {
  /** Apex/root domains (example.com) use an A record. */
  apexARecord: '76.76.21.21',
  /** Subdomains (www.example.com, book.example.com) use a CNAME. */
  cnameTarget: 'cname.vercel-dns.com',
} as const;

export type DomainDnsConfig = {
  apexARecord: string;
  cnameTarget: string;
};

export type DnsRecord = {
  type: 'A' | 'CNAME';
  /** Host/name field at the registrar (@ for apex, or the subdomain label). */
  name: string;
  /** Value/points-to field at the registrar. */
  value: string;
};

/** Strip protocol, path, port, whitespace and lowercase a domain entry. */
export function normalizeCustomDomainInput(raw?: string | null): string {
  let value = (raw ?? '').trim().toLowerCase();
  if (!value) return '';
  value = value.replace(/^https?:\/\//, '');
  value = value.split('/')[0] ?? '';
  value = value.split('?')[0] ?? '';
  value = value.split(':')[0] ?? '';
  return value.trim();
}

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

export function isValidCustomDomain(domain: string): boolean {
  const value = normalizeCustomDomainInput(domain);
  if (!value || value.length > 253) return false;
  return DOMAIN_RE.test(value);
}

/** True for apex/root domains like example.com (exactly two labels). */
export function isApexDomain(domain: string): boolean {
  const value = normalizeCustomDomainInput(domain);
  return value.split('.').filter(Boolean).length === 2;
}

/**
 * A domain goes live when hosting accepted it, or when hosting is not connected
 * and a platform admin is recording it by hand. A failed attach stays requested.
 */
export function domainAttachAllowsGoLive(vercel: { configured: boolean; attached: boolean }): boolean {
  if (!vercel.configured) return true;
  return vercel.attached;
}

export function domainAttachFailureMessage(domain: string, vercelError?: string | null): string {
  const record = dnsRecordsForDomain(domain)[0];
  const dns = record
    ? ` Add a ${record.type} record at the registrar: host ${record.name}, value ${record.value}.`
    : '';
  const reason = vercelError?.trim() ? ` ${vercelError.trim()}` : '';
  return `Could not attach ${domain}.${reason}${dns} It stays requested until the host is attached.`;
}

/** DNS record(s) the owner must add at their registrar for this domain. */
export function dnsRecordsForDomain(
  domain: string,
  config: DomainDnsConfig = DEFAULT_DOMAIN_DNS,
): DnsRecord[] {
  const value = normalizeCustomDomainInput(domain);
  if (!value) return [];
  if (isApexDomain(value)) {
    return [{ type: 'A', name: '@', value: config.apexARecord }];
  }
  const label = value.split('.')[0] ?? value;
  return [{ type: 'CNAME', name: label, value: config.cnameTarget }];
}

/** Public URLs for a tenant on the platform domain (by slug). */
export function buildTenantPlatformUrls(
  origin: string,
  slug: string,
): { siteUrl: string; bookUrl: string; availabilityUrl: string } {
  const base = origin.replace(/\/$/, '');
  const s = encodeURIComponent(slug);
  return {
    siteUrl: `${base}/site?org=${s}`,
    bookUrl: `${base}/book?org=${s}`,
    availabilityUrl: `${base}/availability?org=${s}`,
  };
}

/** Pretty https URL for a live custom domain. */
export function customDomainUrl(domain: string): string {
  const value = normalizeCustomDomainInput(domain);
  return value ? `https://${value}` : '';
}
